import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  ReactNode,
  Dispatch,
} from "react";
import useSyncedReducer from "@shared/hooks/useSyncedReducer";

type MakeContextOptions<S, A> = {
  name: string;
  initialState: S;
  reducer: (state: S, action: A) => S;
  storageKey?: string;
  statePath?: string | string[];
  syncedKeys?: (keyof S)[];
};

export function makeContext<S, A extends { type: string; payload?: any }>(
  options: MakeContextOptions<S, A>
) {
  const { name, initialState, reducer, storageKey, statePath, syncedKeys } =
    options;

  const StateContext = createContext<S | null>(null);
  const DispatchContext = createContext<Dispatch<A> | null>(null);

  function Provider({ children }: { children: ReactNode }) {
    const [state, dispatch] =
      storageKey || syncedKeys
        ? useSyncedReducer<S, A>(reducer, initialState, {
            storageKey: storageKey ?? "__context",
            statePath,
            syncedKeys,
          })
        : useReducer(reducer, initialState);

    return (
      <StateContext.Provider value={state}>
        <DispatchContext.Provider value={dispatch}>
          {children}
        </DispatchContext.Provider>
      </StateContext.Provider>
    );
  }

  function useStateContext(): S {
    const context = useContext(StateContext);
    if (!context) {
      throw new Error(`use${name} must be used within ${name}Provider`);
    }
    return context;
  }

  function useDispatchContext(): Dispatch<A> {
    const context = useContext(DispatchContext);
    if (!context) {
      throw new Error(`use${name}Dispatch must be used within ${name}Provider`);
    }
    return context;
  }

  return {
    Provider,
    useState: useStateContext,
    useDispatch: useDispatchContext,
  };
}

export default makeContext;
